import React from 'react';
import { Leaf, Recycle, Users, TrendingDown, Award, Heart } from 'lucide-react';
import './SustainabilityPage.css';

const SustainabilityPage = () => {
  const commitments = [
    {
      icon: <TrendingDown size={32} />,
      title: 'Emissions Reduction',
      description: 'Committed to reducing greenhouse gas emissions by 40% by 2030 through advanced technologies and operational improvements',
      metrics: '40% reduction target by 2030'
    },
    {
      icon: <Recycle size={32} />,
      title: 'Waste Management',
      description: 'Comprehensive waste reduction and recycling programs minimizing environmental impact across all operations',
      metrics: '85% waste diversion from landfills'
    },
    {
      icon: <Leaf size={32} />,
      title: 'Water Conservation',
      description: 'Advanced water recycling and treatment systems reducing freshwater consumption in refining processes',
      metrics: '60% water recycling rate'
    },
    {
      icon: <Users size={32} />,
      title: 'Community Engagement',
      description: 'Active partnerships with local communities supporting education, economic development, and environmental initiatives',
      metrics: '$5M+ annual community investment'
    }
  ];

  const initiatives = [
    {
      title: 'Renewable Energy Integration',
      image: 'https://images.pexels.com/photos/3192662/pexels-photo-3192662.jpeg',
      description: 'Investing in solar and wind energy to power refinery operations and reduce carbon footprint.'
    },
    {
      title: 'Methane Emission Control',
      image: 'https://images.unsplash.com/photo-1508188609340-e8068b599193?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxvaWwlMjB3b3JrZXJzJTIwc2FmZXR5fGVufDB8fHx8MTc2NTEzMzM2MXww&ixlib=rb-4.1.0&q=85',
      description: 'Advanced monitoring and capture systems preventing methane leaks across production facilities.'
    },
    {
      title: 'Biodiversity Protection',
      image: 'https://images.unsplash.com/photo-1559510981-10719ce4266a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxvaWwlMjBwaXBlbGluZXxlbnwwfHx8fDE3NjUxMzMzNjh8MA&ixlib=rb-4.1.0&q=85',
      description: 'Habitat restoration programs and wildlife protection measures at operational sites.'
    }
  ];

  const achievements = [
    { icon: <Award size={24} />, text: 'ISO 14001 Environmental Management Certification' },
    { icon: <Award size={24} />, text: 'EPA Energy Star Partner recognition' },
    { icon: <Award size={24} />, text: 'Zero discharge to water certifications' },
    { icon: <Award size={24} />, text: 'Industry safety excellence awards' }
  ];

  return (
    <div className="sustainability-page">
      <section className="sustainability-hero">
        <div className="sustainability-hero-overlay"></div>
        <div className="sustainability-hero-content">
          <h1>Sustainability Commitment</h1>
          <p>Building a responsible energy future through environmental stewardship and community partnership</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Our Environmental Strategy</h2>
          <p className="section-subtitle">
            Comprehensive approach to reducing environmental impact while delivering reliable energy
          </p>

          <div className="commitments-grid">
            {commitments.map((commitment, index) => (
              <div key={index} className="commitment-card">
                <div className="commitment-icon">{commitment.icon}</div>
                <h3>{commitment.title}</h3>
                <p>{commitment.description}</p>
                <div className="commitment-metric">{commitment.metrics}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="initiatives-section section">
        <div className="container">
          <h2 className="section-title">Key Initiatives</h2>
          <p className="section-subtitle">
            Active programs driving positive environmental outcomes
          </p>

          <div className="initiatives-grid">
            {initiatives.map((initiative, index) => (
              <div key={index} className="initiative-card">
                <div className="initiative-image">
                  <img src={initiative.image} alt={initiative.title} />
                </div>
                <div className="initiative-content">
                  <h3>{initiative.title}</h3>
                  <p>{initiative.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="community-section">
        <div className="container">
          <div className="community-grid">
            <div className="community-content">
              <Heart size={48} className="community-icon" />
              <h2>Community Development</h2>
              <p>
                We believe in creating lasting positive impact in the communities where we operate. Our programs focus on education, economic opportunity, and local infrastructure development.
              </p>
              <ul className="community-programs">
                <li><strong>Educational Scholarships:</strong> Supporting STEM education for local students</li>
                <li><strong>Skills Training:</strong> Workforce development programs for local employment</li>
                <li><strong>Infrastructure Support:</strong> Investment in community facilities and services</li>
                <li><strong>Emergency Response:</strong> Rapid support during natural disasters and crises</li>
              </ul>
            </div>
            <div className="community-image">
              <img
                src="https://images.pexels.com/photos/7932921/pexels-photo-7932921.jpeg"
                alt="Community Engagement"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Sustainability Reporting</h2>
          <p className="section-subtitle">
            Transparent reporting on our environmental performance and progress
          </p>

          <div className="reporting-content">
            <p>
              We publish comprehensive annual sustainability reports detailing our environmental performance, emissions data, water usage, waste management, and community investment. Our reports follow GRI (Global Reporting Initiative) standards and are independently verified.
            </p>
            <div className="report-cta">
              <button className="btn-primary">Download Latest Report</button>
              <button className="btn-secondary">View Archive</button>
            </div>
          </div>
        </div>
      </section>

      <section className="achievements-section section">
        <div className="container">
          <h2 className="section-title">Recognition & Certifications</h2>
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div key={index} className="achievement-item">
                {achievement.icon}
                <span>{achievement.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;